import React from "react";
import { Modal } from "antd";
import { Button } from "@pc/ds-react";
import { useIsMobile } from "src/hooks";
import "./UIModal.scss";

export interface UIModalProps {
  open: boolean;
  onClose?: () => void;
  title?: string;
  body?: string;
  footer?: string;
  primaryButton?: { label: string; onClick: () => void };
  secondaryButton?: { label: string; onClick: () => void };
  encouragementText?: string;
  supportText?: string;
  disclaimerNode?: React.ReactNode;
  children?: React.ReactNode;
  closable?: boolean;
  width?: number;
  className?: string;
}

const MEGAPHONE_IMG_SRC = "/images/megaphone.png";

const UIModal: React.FC<UIModalProps> = ({
  open,
  onClose,
  title,
  body,
  footer,
  primaryButton,
  secondaryButton,
  encouragementText,
  supportText,
  disclaimerNode,
  children,
  closable = true,
  width: modalWidth = 600,
  className,
}) => {
  const isMobile = useIsMobile(740);
  const hasEncouragementLayout = !!encouragementText;

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={closable}
      mask={{ closable: false }}
      width={isMobile ? "100%" : modalWidth}
      className={`ui-modal${className ? ` ${className}` : ""}`}
      wrapClassName={isMobile ? "ui-modal-wrap-mobile" : ""}
      centered={!isMobile}
      transitionName={isMobile ? "slide-up-mobile" : undefined}
      maskTransitionName={isMobile ? "fade" : undefined}
      destroyOnHidden
    >
      <div dir="rtl">
        {title && (
          <>
            <div className="ui-modal__header">
              <h2 className="ui-modal__title">{title}</h2>
            </div>
            <div className="ui-modal__divider" />
          </>
        )}
        {children ? (
          <div className="ui-modal__content">{children}</div>
        ) : (
          <div className="ui-modal__content">
            {hasEncouragementLayout && (
              <p className="ui-modal__body">{body}</p>
            )}
            <div className="ui-modal__icon-wrap">
              <img
                src={MEGAPHONE_IMG_SRC}
                alt=""
                className="ui-modal__icon"
                aria-hidden
              />
            </div>
            {!hasEncouragementLayout && (
              <p className="ui-modal__body">{body}</p>
            )}
            {hasEncouragementLayout && (
              <>
                <p className="ui-modal__encouragement">{encouragementText}</p>
                {supportText && (
                  <p className="ui-modal__support-text">{supportText}</p>
                )}
              </>
            )}
            {footer && !hasEncouragementLayout ? (
              <p className="ui-modal__footer">{footer}</p>
            ) : null}
            {(primaryButton || secondaryButton) ? (
              <div className="ui-modal__actions">
                {primaryButton && (
                  <Button
                    onClick={primaryButton.onClick}
                    variant="primary"
                    fullWidth
                    showEndIcon
                  >
                    {primaryButton.label}
                  </Button>
                )}
                {secondaryButton && (
                  <Button
                    onClick={secondaryButton.onClick}
                    variant="secondary"
                    fullWidth
                  >
                    {secondaryButton.label}
                  </Button>
                )}
              </div>
            ) : null}
            {disclaimerNode && (
              <div className="ui-modal__disclaimer">{disclaimerNode}</div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UIModal;
